/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2025.
 */

import { SEO_OBFUSCATION_FIELDS } from '@/data/constants/seo';
import { isPublic } from '@/data/utils/isPublic';
import { deobfuscate, isObfuscated } from '@/data/utils/seoObfuscator';
import { Middleware, SWRHook, unstable_serialize, useSWRConfig } from 'swr';

const canMutateObject = (obj: any): boolean => {
	if (typeof obj !== 'object' || obj === null) return false;
	if (Object.isFrozen(obj) || !Object.isExtensible(obj)) return false;
	try {
		const probe = '__seo_probe_' + Date.now();
		(obj as any)[probe] = true;
		delete (obj as any)[probe];
		return true;
	} catch {
		return false;
	}
};
/**
 * Middleware to unescape SEO obfuscated fields in the SWR fallback.
 */
export const seoObfuscatorMiddleWare: Middleware =
	(useSWRNext: SWRHook) => (key, fetcher, config) => {
		const serializedKey = unstable_serialize(key);
		// 1. Get access to the global cache
		const { cache } = useSWRConfig();
		const swr = useSWRNext(key, fetcher, config);
		// 2. Check the cache for this key
		// Note: cache.get(key) returns the internal state object
		// (usually { data, error, ... } or undefined if empty)
		const cachedState = cache.get(serializedKey);
		const hasCachedData = cachedState && cachedState.data !== undefined;

		// 3. Determine Source
		const isFallback = !hasCachedData && swr.data !== undefined;
		const data = swr.data as any;
		if (isFallback && data && isObfuscated(data)) {
			const deobfuscatedData = deobfuscate(data, SEO_OBFUSCATION_FIELDS);

			if (isPublic()) {
				// client-side: mutate the existing fallback data in-place if possible
				if (canMutateObject(data)) {
					Object.assign(data, deobfuscatedData);
					// Keep original reference; no need to return a new swr object.
					return swr;
				} else {
					// Provide a fresh merged object; guarantees consumers see updated fields.
					return { ...swr, data: { ...data, ...deobfuscatedData } };
				}
			} else {
				/**
				 * Non-public: server-side pre-rendering,
				 * expose deobfuscatedData separately without mutating fallback.
				 * This ensures server-side retains obfuscated data to send to client.
				 */
				return { ...swr, data: deobfuscatedData };
			}
		} else {
			return swr;
		}
	};
